/** Basic organization types (aligned with backend). */
export const BASIC_ORG = {
  POLICE: 1,
  HOSPITAL: 2,
  INSURANCE: 3,
}

export const ORGANIZATION_FIELD_KEY = 'organization'

export const BASIC_TO_ROLE = {
  [BASIC_ORG.POLICE]: 'police',
  [BASIC_ORG.HOSPITAL]: 'hospital',
  [BASIC_ORG.INSURANCE]: 'insurance',
}

export const ROLE_TO_BASIC = {
  police: BASIC_ORG.POLICE,
  hospital: BASIC_ORG.HOSPITAL,
  insurance: BASIC_ORG.INSURANCE,
}

export const KIND_TO_BASIC = {
  POLICE: BASIC_ORG.POLICE,
  HOSPITAL: BASIC_ORG.HOSPITAL,
  INSURANCE: BASIC_ORG.INSURANCE,
}

export const BASIC_TO_KIND = {
  [BASIC_ORG.POLICE]: 'POLICE',
  [BASIC_ORG.HOSPITAL]: 'HOSPITAL',
  [BASIC_ORG.INSURANCE]: 'INSURANCE',
}

const EMPTY_CRASH_BY_ROLE = {
  police: null,
  hospital: null,
  insurance: null,
}

export function createEmptyCrashByRole () {
  return { ...EMPTY_CRASH_BY_ROLE }
}

/** Match grid targets for the two organizations other than the source. */
export function getMatchTargetsForSource (sourceBasic) {
  return [BASIC_ORG.POLICE, BASIC_ORG.HOSPITAL, BASIC_ORG.INSURANCE].filter(
    (basic) => basic !== sourceBasic
  )
}

export function hasCrashRoleData (detail) {
  if (!detail || typeof detail !== 'object') return false
  return Object.values(detail).some((v) => {
    if (v == null) return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'object') return Object.keys(v).length > 0
    return Boolean(v)
  })
}

function asPeopleArray (collection) {
  if (collection == null) return []
  if (Array.isArray(collection)) return collection
  if (typeof collection === 'object') return Object.values(collection)
  return []
}

export function enrichCrashDetail (crash) {
  if (!crash) return null

  const rootPeople = asPeopleArray(crash.people).map((person, personIndex) => ({
    ...person,
    personIndex,
  }))

  const units = asPeopleArray(crash.units)
  if (units.length > 0) {
    const fromUnits = units.flatMap((unit, unitIndex) =>
      asPeopleArray(unit?.people).map((person, personIndex) => ({
        ...person,
        unitIndex,
        personIndex,
      })),
    )
    return {
      ...crash,
      people: [...rootPeople, ...fromUnits],
    }
  }

  return {
    ...crash,
    people: rootPeople.length > 0 ? rootPeople : crash.people,
  }
}

/**
 * @param {unknown} img
 * @returns {boolean}
 */
export function isMergeImageReference (img) {
  if (typeof img !== 'string' || !img.trim()) return false
  if (img.startsWith('data:image/')) return true
  if (img.startsWith('attachments/')) return true
  if (img.startsWith('http://') || img.startsWith('https://')) return true
  return false
}

/**
 * @param {string[]} images
 * @returns {string[]}
 */
export function dedupeMergeImages (images = []) {
  const seen = new Set()
  return images.filter((img) => {
    if (!isMergeImageReference(img)) return false
    if (seen.has(img)) return false
    seen.add(img)
    return true
  })
}

/**
 * Raccoglie, deduplica e filtra le immagini da tutte le fonti del merge.
 *
 * @param {object} params
 * @param {string[]} [params.policePhotos] - Foto caricate come data URL dalla fonte polizia
 * @param {object} [params.policeDetail] - Oggetto crash polizia (puo contenere crash.images)
 * @param {object} [params.hospitalDetail] - Oggetto crash ospedale
 * @param {object} [params.insuranceDetail] - Oggetto crash assicurazione
 * @returns {string[]} Immagini merge (base64, path attachments o URL), senza duplicati
 */
export function collectMergeImages ({
  policePhotos,
  policeDetail,
  hospitalDetail,
  insuranceDetail,
} = {}) {
  const sources = [
    ...(policePhotos || []),
    ...(policeDetail?.crash?.images || []),
    ...(hospitalDetail?.crash?.images || []),
    ...(insuranceDetail?.crash?.images || []),
  ]

  const skipped = sources.filter(
    (img) => img != null && img !== '' && !isMergeImageReference(img),
  )
  if (skipped.length > 0) {
    console.warn(
      `[collectMergeImages] skipped ${skipped.length} unsupported image reference(s)`,
      skipped,
    )
  }

  const result = dedupeMergeImages(sources.filter(Boolean))

  if (process.env.NODE_ENV === 'development') {
    console.debug(
      `[collectMergeImages] sources: policePhotos=${policePhotos?.length ?? 0}, policeDetail=${policeDetail?.crash?.images?.length ?? 0}, hospital=${hospitalDetail?.crash?.images?.length ?? 0}, insurance=${insuranceDetail?.crash?.images?.length ?? 0} -> final=${result.length}`
    )
  }

  return result
}

export function normalizeInsuranceMatch (myMatch) {
  if (!myMatch) return null
  return {
    ...myMatch,
    units: myMatch?.units?.map((unit) => ({
      ...unit,
      people: [],
    })),
    people: myMatch?.people?.length
      ? myMatch.people
      : myMatch?.units?.flatMap((u, ui) =>
        u?.people?.flatMap((p, pi) => ({ ...p, unitIndex: ui, personIndex: pi }))
      ),
  }
}

export function isOrganizationFieldKey (fieldKey) {
  return fieldKey === ORGANIZATION_FIELD_KEY
}

/**
 * Label fissa per la categoria basic (1 Police, 2 Hospital, 3 Insurance).
 * Non usa il nome della prima organizzazione registrata (es. "Pohnpei State Police").
 *
 * @param {number|string} basicId
 * @param {Array<object>} [_organizations] - ignorato (compat firma call-site)
 * @param {{ police?: string, hospital?: string, insurance?: string }} [fallbacks] - label i18n
 * @returns {string}
 */
export function getOrgLabelByBasic (basicId, _organizations = [], fallbacks = {}) {
  const id = Number(basicId)
  if (!Number.isFinite(id)) return ''
  if (id === BASIC_ORG.POLICE) return fallbacks.police ?? 'Police'
  if (id === BASIC_ORG.HOSPITAL) return fallbacks.hospital ?? 'Hospital'
  if (id === BASIC_ORG.INSURANCE) return fallbacks.insurance ?? 'Insurance'
  return ''
}

/**
 * Label organizzazione foglia per id (`organizations[].value` / `.id`).
 * @param {number|string} orgId
 * @param {Array<object>} [organizations]
 * @returns {string}
 */
export function getOrganizationLabelById (orgId, organizations = []) {
  const id = Number(orgId)
  if (!Number.isFinite(id)) return ''
  const list = Array.isArray(organizations) ? organizations : Object.values(organizations)
  const org = list.find((o) => Number(o?.value ?? o?.id) === id)
  return org?.label ?? org?.description ?? ''
}

/**
 * Valore campo form `organization`: preferisce nome org foglia, altrimenti categoria basic.
 * @param {unknown} value
 * @param {Array<object>} [organizations]
 * @param {{ police?: string, hospital?: string, insurance?: string }} [fallbacks]
 * @returns {string}
 */
export function formatOrganizationFieldValue (value, organizations = [], fallbacks = {}) {
  if (value == null || value === '') return ''
  const leafLabel = getOrganizationLabelById(value, organizations)
  if (leafLabel) return leafLabel
  return getOrgLabelByBasic(value, organizations, fallbacks) || String(value)
}
